/** Pure helpers for the operational map – kept free of React and MapLibre so they are trivially testable. */

export const TONE_HEX = {
  neutral: "#94a3b8",
  info: "#0ea5e9",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#f43f5e",
  primary: "#2670F2",
};

/** Bounding box [[minLng,minLat],[maxLng,maxLat]] around points ({lat,lng}); null if none. */
export function boundsOf(points, minSpan = 0.15) {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (!pts.length) return null;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of pts) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  // never zoom into a single point – keep some geography around it
  if (maxLat - minLat < minSpan) {
    const c = (minLat + maxLat) / 2;
    minLat = c - minSpan / 2;
    maxLat = c + minSpan / 2;
  }
  if (maxLng - minLng < minSpan * 2) {
    const c = (minLng + maxLng) / 2;
    minLng = c - minSpan;
    maxLng = c + minSpan;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/**
 * Quadratic-bezier arc between two lng/lat points (32 samples). Curves to the right of the travel direction so
 * out- and return legs between the same two places do not overlap.
 */
export function arc(from, to, bulge = 0.18, samples = 32) {
  const ax = from.lng, ay = from.lat, bx = to.lng, by = to.lat;
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  // perpendicular (lat compressed by cos(lat) so the curve looks round on screen)
  const k = Math.cos((my * Math.PI) / 180);
  const px = -dy / len, py = (dx / len) * k;
  const cx = mx + px * len * bulge, cy = my + py * len * bulge;
  const out = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const x = (1 - t) * (1 - t) * ax + 2 * (1 - t) * t * cx + t * t * bx;
    const y = (1 - t) * (1 - t) * ay + 2 * (1 - t) * t * cy + t * t * by;
    out.push([x, y]);
  }
  return out;
}

/** Point at t=0.5 of the arc – where the transport chip sits. */
export function arcMidpoint(from, to, bulge = 0.18) {
  const pts = arc(from, to, bulge, 2);
  return { lng: pts[1][0], lat: pts[1][1] };
}

/**
 * Declutter markers that would overlap at the current zoom: greedy clustering in screen space, then a small circular
 * spread (pixel offsets) inside each cluster. `project` maps {lat,lng} → {x,y} pixels.
 * Returns { [id]: { offset: [dx, dy], clusterSize } }.
 */
export function layoutMarkers(points, project, radiusPx = 30) {
  const out = {};
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (!project || !pts.length) {
    for (const p of pts) out[p.id] = { offset: [0, 0], clusterSize: 1 };
    return out;
  }
  const px = pts.map((p) => ({ p, ...project(p) }));
  const assigned = new Set();
  const clusters = [];
  for (let i = 0; i < px.length; i++) {
    if (assigned.has(i)) continue;
    const cluster = [i];
    assigned.add(i);
    for (let j = i + 1; j < px.length; j++) {
      if (assigned.has(j)) continue;
      if (Math.hypot(px[i].x - px[j].x, px[i].y - px[j].y) < radiusPx) {
        cluster.push(j);
        assigned.add(j);
      }
    }
    clusters.push(cluster);
  }
  for (const c of clusters) {
    if (c.length === 1) {
      out[px[c[0]].p.id] = { offset: [0, 0], clusterSize: 1 };
      continue;
    }
    const cx = c.reduce((s, i) => s + px[i].x, 0) / c.length;
    const cy = c.reduce((s, i) => s + px[i].y, 0) / c.length;
    const r = c.length === 2 ? 19 : 18 + c.length * 3;
    c.forEach((i, k) => {
      const angle = c.length === 2 ? Math.PI * k : -Math.PI / 2 + (2 * Math.PI * k) / c.length;
      const tx = cx + Math.cos(angle) * r, ty = cy + Math.sin(angle) * r;
      out[px[i].p.id] = { offset: [Math.round(tx - px[i].x), Math.round(ty - px[i].y)], clusterSize: c.length };
    });
  }
  return out;
}

/**
 * Greedy screen-space clustering: points closer than `radiusPx` collapse into one group with a centroid.
 * Returns [{ id, lat, lng, items: [...points] }]. Single points come back as groups of one.
 */
export function clusterPoints(points, project, radiusPx = 44) {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (!project) return pts.map((p) => ({ id: `c-${p.id}`, lat: p.lat, lng: p.lng, items: [p] }));
  const px = pts.map((p) => ({ p, ...project(p) }));
  const assigned = new Set();
  const groups = [];
  for (let i = 0; i < px.length; i++) {
    if (assigned.has(i)) continue;
    const members = [i];
    assigned.add(i);
    let cx = px[i].x, cy = px[i].y;
    for (let j = i + 1; j < px.length; j++) {
      if (assigned.has(j)) continue;
      if (Math.hypot(cx - px[j].x, cy - px[j].y) < radiusPx) {
        members.push(j);
        assigned.add(j);
        cx = members.reduce((s, k) => s + px[k].x, 0) / members.length;
        cy = members.reduce((s, k) => s + px[k].y, 0) / members.length;
      }
    }
    const items = members.map((k) => px[k].p);
    groups.push({ id: `c-${items.map((p) => p.id).sort().join("|")}`, lat: items.reduce((s, p) => s + p.lat, 0) / items.length, lng: items.reduce((s, p) => s + p.lng, 0) / items.length, items });
  }
  return groups;
}

/** Sum of the alert counters on a map location. */
export const alertCount = (loc) => {
  const a = loc?.alerts;
  if (!a) return 0;
  return (a.issues_open || 0) + (a.service_overdue || 0) + (a.conflicts || 0);
};

export const projectTone = (status) => {
  if (["i_drift", "overlevert"].includes(status)) return "success";
  if (["klargjoring", "transport", "montasje", "demontering"].includes(status)) return "warning";
  if (["planlegging", "prosjektering"].includes(status)) return "info";
  return "neutral";
};
